/**
 * Página de Itens de Checklist - ConfereAI
 * Reestruturada para usar componentes padronizados
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services';
import { utils } from '../../utils';
import { ApiResponse } from '../../types/api';
import {
  PageHeader,
  Card,
  CardHeader,
  CardBody,
  DataTable,
  Column,
  ActionButtons,
  StatCard,
  EmptyState,
  FilterPanel,
} from '../../components/Common';
import ResultsCounter from '../../components/Common/ResultsCounter';
import LoadMoreButton from '../../components/Common/LoadMoreButton';
import { usePermissions } from '../../hooks/usePermissions';
import { SystemModule } from '../../types/permissions';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import ImportModal from '../../components/Common/ImportModal';
import { itensChecklistImportConfig } from '../../config/importConfigs';

// Interface alinhada com a estrutura real do banco de dados PostgreSQL
interface ChecklistItem {
  id: number;
  checklist_id: number | null;
  categoria: string | null;
  descricao: string;
  tipo: string;
  opcoes: any | null;
  obrigatorio: boolean;
  ordem: number | null;
  valor: string | null;
  observacao: string | null;
  fotos: any | null;
  criado_em: string;
  atualizado_em: string;
  empresa_id: number;
}

const ChecklistItems: React.FC = () => {
  const navigate = useNavigate();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [showImportModal, setShowImportModal] = useState(false);
  const [filter, setFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Hook de infinite scroll
  const {
    items,
    isLoading,
    totalItems,
    displayedItems,
    hasMore,
    loadMore
  } = useInfiniteScroll<ChecklistItem>({
    fetchFunction: async (page, limit) => {
      try {
        const response = await apiService.getChecklistItemsManagement();

        if (response.success && response.data) {
          const apiItems = (response.data as any).itens;
          const mappedItems: ChecklistItem[] = apiItems.map((item: any) => ({
            id: item.id,
            checklist_id: item.checklist_id,
            categoria: item.categoria,
            descricao: item.descricao,
            tipo: item.tipo,
            opcoes: item.opcoes,
            obrigatorio: item.obrigatorio,
            ordem: item.ordem,
            valor: item.valor,
            observacao: item.observacao,
            fotos: item.fotos,
            criado_em: item.criado_em,
            atualizado_em: item.atualizado_em,
            empresa_id: item.empresa_id
          }));

          return {
            success: true,
            data: {
              items: mappedItems,
              pagination: {
                total: mappedItems.length
              }
            }
          };
        }

        throw new Error('Resposta inválida da API');
      } catch (error) {
        console.error('Erro ao carregar itens de checklist:', error);
        return {
          success: false,
          data: [],
          message: 'Erro ao carregar itens de checklist'
        };
      }
    },
    itemsPerPage: 10,
    onError: (error) => {
      utils.showNotification(error, { type: 'error' });
    }
  });

  const getObrigatorioClass = (obrigatorio: boolean): string => {
    return obrigatorio ? 'status-success' : 'status-warning';
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.descricao.toLowerCase().includes(filter.toLowerCase()) ||
                         (item.categoria && item.categoria.toLowerCase().includes(filter.toLowerCase())) ||
                         (item.observacao && item.observacao.toLowerCase().includes(filter.toLowerCase()));
    const matchesCategory = !categoryFilter || item.categoria === categoryFilter;
    const matchesType = !typeFilter || item.tipo === typeFilter;
    return matchesSearch && matchesCategory && matchesType;
  });

  const deleteItem = async (id: number) => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm('Tem certeza que deseja excluir este item?')) {
      try {
        const response = await apiService.deleteChecklistItem(id) as ApiResponse<{ message: string }>;
        if (response.success) {
          utils.showNotification('Item excluído com sucesso!', { type: 'success' });
          // Recarrega a lista após deletar
          window.location.reload();
        } else {
          throw new Error(response.message || 'Erro ao excluir item');
        }
      } catch (error) {
        console.error('Erro ao excluir item:', error);
        utils.showNotification('Erro ao excluir item', { type: 'error' });
      }
    }
  };

  const getCategories = () => Array.from(new Set(items.map(i => i.categoria).filter((c): c is string => c !== null)));
  const getTypes = () => Array.from(new Set(items.map(i => i.tipo)));

  const formatTipo = (tipo: string): string => {
    const tipoMap: Record<string, string> = {
      'texto': 'Texto',
      'numero': 'Número',
      'selecao': 'Seleção',
      'multipla_escolha': 'Múltipla Escolha',
      'foto': 'Foto',
      'assinatura': 'Assinatura'
    };
    return tipoMap[tipo] || tipo;
  };

  const columns: Column<ChecklistItem>[] = [
    {
      header: 'Ordem',
      accessor: 'ordem',
      render: (value: number | null) => value ?? '-',
    },
    {
      header: 'Descrição',
      accessor: 'descricao',
      render: (value: string, row: ChecklistItem) => (
        <div>
          <strong>{value}</strong>
          {row.observacao && (
            <div style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
              {row.observacao}
            </div>
          )}
        </div>
      ),
    },
    {
      header: 'Categoria',
      accessor: 'categoria',
      render: (value: string | null) => (
        value ? <span className="status-badge status-info">{value}</span> : '-'
      ),
    },
    {
      header: 'Tipo',
      accessor: 'tipo',
      render: (value: string) => <span className="status-badge status-secondary">{formatTipo(value)}</span>,
    },
    {
      header: 'Obrigatório',
      accessor: 'obrigatorio',
      render: (value: boolean) => (
        <span className={`status-badge ${getObrigatorioClass(value)}`}>
          {value ? 'Sim' : 'Não'}
        </span>
      ),
    },
    {
      header: 'Ações',
      accessor: (row: ChecklistItem) => (
        <ActionButtons
          onView={() => navigate(`/itens-checklist/visualizar/${row.id}`)}
          onEdit={() => navigate(`/itens-checklist/editar/${row.id}`)}
          onDelete={() => deleteItem(row.id)}
          showEdit={canUpdate(SystemModule.ITENS_CHECKLIST)}
          showDelete={canDelete(SystemModule.ITENS_CHECKLIST)}
        />
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="page-container">
        <PageHeader title="Itens de Checklist" buttonText="Novo Item" />
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando itens...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <PageHeader title="Itens de Checklist">
        <div style={{ display: 'flex', gap: '10px' }}>
          {canCreate(SystemModule.ITENS_CHECKLIST) && (
            <button
              className="btn btn-secondary"
              onClick={() => setShowImportModal(true)}
            >
              <i className="fas fa-file-excel"></i>
              Importar Planilha
            </button>
          )}
          {canCreate(SystemModule.ITENS_CHECKLIST) && (
            <button
              className="btn btn-primary"
              onClick={() => navigate('/itens-checklist/novo')}
            >
              <i className="fas fa-plus"></i>
              Novo Item
            </button>
          )}
        </div>
      </PageHeader>

      <div className="page-content">
        <Card>
          <CardHeader style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Gerenciamento de Itens</h2>
            <FilterPanel
              fields={[
                {
                  type: 'text',
                  label: 'Buscar',
                  value: filter,
                  onChange: setFilter,
                  placeholder: 'Item, descrição ou grupo...'
                },
                {
                  type: 'select',
                  label: 'Categoria',
                  value: categoryFilter,
                  onChange: setCategoryFilter,
                  options: [
                    { value: '', label: 'Todas' },
                    ...getCategories().map(category => ({ value: category, label: category }))
                  ]
                },
                {
                  type: 'select',
                  label: 'Tipo',
                  value: typeFilter,
                  onChange: setTypeFilter,
                  options: [
                    { value: '', label: 'Todos' },
                    ...getTypes().map(type => ({ value: type, label: type }))
                  ]
                }
              ]}
              onFilter={() => {}}
              onClear={() => {
                setFilter('');
                setCategoryFilter('');
                setTypeFilter('');
              }}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters(!showFilters)}
              title="Filtros de Itens"
            />
          </CardHeader>

          <CardBody>
            {filteredItems.length === 0 ? (
              <EmptyState
                icon="fas fa-list-check"
                title="Nenhum item encontrado"
                description={filter || categoryFilter || typeFilter ? 'Tente ajustar os filtros de busca.' : 'Cadastre os primeiros itens do checklist.'}
                actionText="Cadastrar Primeiro Item"
                onAction={() => navigate('/itens-checklist/novo')}
              />
            ) : (
              <>
                {/* Contador de resultados */}
                {filteredItems.length > 0 && (
                  <ResultsCounter
                    displayedItems={displayedItems}
                    totalItems={totalItems}
                    isLoading={isLoading}
                    label="itens"
                  />
                )}

                <DataTable<ChecklistItem>
                  columns={columns}
                  data={filteredItems}
                  emptyMessage="Nenhum item encontrado"
                  isLoadingMore={isLoading}
                />
                <LoadMoreButton
                  onClick={loadMore}
                  isLoading={isLoading}
                  hasMore={hasMore}
                />
              </>
            )}
          </CardBody>
        </Card>

        <div className="dashboard-grid" style={{ marginTop: '20px' }}>
          <StatCard icon="fas fa-list-check" value={items.length} label="Total de Itens" color="primary" />
          <StatCard icon="fas fa-check-circle" value={items.filter(i => i.obrigatorio).length} label="Itens Obrigatórios" color="info" />
          <StatCard icon="fas fa-camera" value={items.filter(i => i.tipo === 'foto').length} label="Itens com Foto" color="warning" />
          <StatCard icon="fas fa-star" value={items.filter(i => !i.obrigatorio).length} label="Itens Opcionais" color="danger" />
        </div>

        <Card style={{ marginTop: '20px' }}>
          <CardHeader><h3>Distribuição por Categoria</h3></CardHeader>
          <CardBody>
            {getCategories().length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                {getCategories().map(category => {
                  const categoryItems = items.filter(i => i.categoria === category);
                  const percentage = Math.round((categoryItems.length / items.length) * 100);
                  return (
                    <div key={category} style={{ padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <span style={{ fontWeight: '600', color: '#2c3e50' }}>{category}</span>
                        <span style={{ color: '#04C64F', fontWeight: 'bold' }}>{categoryItems.length}</span>
                      </div>
                      <div style={{ background: '#e9ecef', borderRadius: '4px', height: '8px' }}>
                        <div style={{ background: 'linear-gradient(135deg, #04C64F, #03a542)', height: '100%', borderRadius: '4px', width: `${percentage}%` }}></div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px' }}>
                        <span style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>{percentage}% do total</span>
                        <span style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>{categoryItems.filter(i => i.obrigatorio).length} obrigatórios</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ textAlign: 'center', color: '#7f8c8d' }}>Nenhuma categoria disponível</p>
            )}
          </CardBody>
        </Card>
      </div>

      <ImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={() => window.location.reload()}
        config={itensChecklistImportConfig}
      />
    </div>
  );
};

export default ChecklistItems;
